import React, { cloneElement, useEffect, useState } from "react";

import {
  faDownload,
  faExclamationCircle,
  faFile,
  faSpinner,
} from "@fortawesome/pro-regular-svg-icons";
import { useQuery } from "@tanstack/react-query";
import { ColumnDef, Row } from "@tanstack/react-table";
import papaparse from "papaparse";
import { read as xlsxRead, utils as xlsxUtils } from "xlsx";

import { GenericObject } from "src/api/flowTypes";
import { FloatingWindow } from "src/base-components/FloatingWindow/FloatingWindow";
import { Icon } from "src/base-components/Icon";
import { Pill } from "src/base-components/Pill";
import { TableComp } from "src/base-components/Table";
import { FileAttributes } from "src/clients/files";
import { ObjectDetailPane } from "src/dataTable/ObjectDetailPane";
import { ErrorBoundary } from "src/utils/ErrorBoundary";
import { humanReadableSize } from "src/utils/files";
import { LooseLiteral } from "src/utils/typeUtils";

const contentTypes = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "text/csv",
  "text/plain",
  "text/html",
  "application/json",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
] as const;

type KnownContentType = (typeof contentTypes)[number];

// Loosely typed content type, allowing for unknown content types
type ContentType = LooseLiteral<KnownContentType>;

type FileViewerProps = {
  // File Viewer accepts either FileAttributes or File or undefined:
  // FileAttributes is a Files API response
  // File is a locally uploaded file
  // undefined is a placeholder file
  file: FileAttributes | File | undefined;
  children: React.ReactElement;
};

export const FileDownloadButton: React.FC<{ file: FileAttributes | File }> = ({
  file,
}) => {
  const href = useFileUrl(file);

  if (!href) {
    return null;
  }

  return (
    <a
      aria-label={`Download ${file.name}`}
      className="cursor-pointer pr-1 text-gray-500 hover:text-gray-800"
      download={file.name}
      href={href}
      onClick={(e) => e.stopPropagation()}
    >
      <Icon icon={faDownload} size="xs" />
    </a>
  );
};

const getContentType = (file: FileAttributes | File): ContentType => {
  if (file instanceof File) {
    return file.type;
  }

  return file.content_type;
};

const useFileUrl = (file: FileAttributes | File): string | null => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      setObjectUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setObjectUrl(file.download_url ?? null);
    }
  }, [file]);

  return objectUrl;
};

const FileViewer: React.FC<FileViewerProps> = ({ file, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const openFloatingWindow = () => {
    setIsOpen(true);
  };

  const triggerWithClick = children
    ? cloneElement(children, {
        onClick: openFloatingWindow,
      })
    : null;

  let isPlaceholderFile = false;
  if (file === undefined) {
    file = {
      name: "Placeholder file",
      size: 0,
      content_type: "text/plain",
      download_url: null,
    } as FileAttributes;
    isPlaceholderFile = true;
  }

  const contentType = getContentType(file);
  const isFilePDF = contentType === "application/pdf";
  const isFileImage =
    contentType === "image/jpeg" ||
    contentType === "image/png" ||
    contentType === "image/webp";

  return (
    <>
      <FloatingWindow
        button={triggerWithClick}
        height="h-112"
        isOpen={isOpen}
        minHeight="h-64"
        minWidth="w-96"
        pinnable={false}
        title={
          <div className="flex items-center gap-1" data-loc="file-viewer-title">
            <span className="pr-2 font-inter-normal-12px text-gray-800">
              {file.name}
            </span>
            <Pill size="sm" variant="gray">
              {humanReadableSize(file.size)}
            </Pill>
          </div>
        }
        titleRight={isFilePDF ? null : <FileDownloadButton file={file} />}
        draggable
        maximizable
        resizeable
        onClose={() => setIsOpen(false)}
      >
        <ErrorBoundary
          fallbackErrorContent={
            <FallbackViewer message={undefined} state="error" />
          }
        >
          {isPlaceholderFile && <FallbackViewer state="placeholder" />}
          {isFileImage && <ImageViewer file={file} />}
          {isFilePDF && <PDFViewer file={file} />}
          {contentType === "text/csv" && <CSVViewer file={file} />}
          {contentType === "text/plain" && <TextViewer file={file} />}
          {contentType === "text/html" && <HTMLViewer file={file} />}
          {contentType === "application/json" && <JSONViewer file={file} />}
          {contentType ===
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" && (
            <XLSXViewer file={file} />
          )}
          {!contentTypes.includes(contentType as KnownContentType) && (
            <FallbackViewer
              message="File type isn't supported for viewing. Download to view it."
              state="error"
            />
          )}
        </ErrorBoundary>
      </FloatingWindow>
    </>
  );
};

const HTMLViewer: React.FC<{ file: FileAttributes | File }> = ({ file }) => {
  const [hasError, setHasError] = useState(false);
  const [html, setHtml] = useState("");
  const fileUrl = useFileUrl(file);

  useEffect(() => {
    // Reset state when fileUrl changes
    setHasError(false);
    setHtml("");

    const ac = new AbortController();

    if (!fileUrl) {
      return;
    }

    const fetchHtml = async () => {
      try {
        const response = await fetch(fileUrl, { signal: ac.signal });

        if (!response.ok) {
          throw new Error("Failed to fetch HTML file");
        }

        const html = await response.text();

        setHtml(html);
      } catch {
        if (!ac.signal.aborted) {
          setHasError(true);
        }
      }
    };

    fetchHtml();

    return () => {
      ac.abort();
    };
  }, [fileUrl]);

  if (!fileUrl) {
    return <FallbackViewer message="HTML file not found" state="error" />;
  }

  if (hasError) {
    return (
      <FallbackViewer message="HTML file could not be loaded" state="error" />
    );
  }

  return (
    <div className="h-full w-full overflow-auto p-4">
      <div className="flex h-full min-h-[1px] min-w-full items-center justify-center">
        <iframe
          className="h-full w-full border-none object-contain"
          data-loc="html-viewer"
          sandbox=""
          srcDoc={html}
          title={file.name}
          onError={() => setHasError(true)}
        />
      </div>
    </div>
  );
};

const ImageViewer: React.FC<{ file: FileAttributes | File }> = ({ file }) => {
  const [hasError, setHasError] = useState(false);
  const fileUrl = useFileUrl(file);

  if (!fileUrl) {
    return <FallbackViewer message="Image not found" state="error" />;
  }

  if (hasError) {
    return <FallbackViewer message="Image could not be loaded" state="error" />;
  }

  return (
    <div className="h-full w-full overflow-auto p-4">
      <div className="flex min-h-full min-w-full items-center justify-center">
        <img
          alt={file.name}
          className="object-contain"
          src={fileUrl}
          style={{ maxWidth: "100%", maxHeight: "100%" }}
          onError={() => setHasError(true)}
        />
      </div>
    </div>
  );
};

const PDFViewer: React.FC<{ file: FileAttributes | File }> = ({ file }) => {
  const [pdfDataUrl, setPdfDataUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fileUrl = useFileUrl(file);

  useEffect(() => {
    setIsLoading(true);
    setError(null);

    if (!fileUrl) {
      setError("PDF url is not available");
      setIsLoading(false);
      return;
    }

    if (file instanceof File) {
      setPdfDataUrl(fileUrl);
      setIsLoading(false);
    } else {
      fetch(fileUrl)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to fetch PDF: ${response.status}`);
          }
          return response.blob();
        })
        .then((blob) => {
          const dataUrl = URL.createObjectURL(blob);
          setPdfDataUrl(dataUrl);
          setIsLoading(false);
        })
        .catch((err) => {
          setError(err.message);
          setIsLoading(false);
        });
    }
  }, [fileUrl, file]);

  useEffect(() => {
    return () => {
      if (pdfDataUrl && !fileUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(pdfDataUrl);
      }
    };
  }, [pdfDataUrl, fileUrl]);

  if (isLoading) {
    return <FallbackViewer message={undefined} state="loading" />;
  }

  if (error || !pdfDataUrl) {
    return <FallbackViewer message={undefined} state="error" />;
  }

  return (
    <iframe
      className="h-full w-full border-0"
      data-loc="pdf-viewer"
      src={pdfDataUrl + "#view=FitH"}
      title={file.name ?? ""}
    />
  );
};

const CSVViewer: React.FC<{ file: FileAttributes | File }> = ({ file }) => {
  const [data, setData] = useState<Record<string, string>[]>([
    {
      x: "1",
      y: "2",
      z: "3",
      w: "4",
    },
  ]);
  const [columns, setColumns] = useState<ColumnDef<Record<string, string>>[]>([
    {
      accessorKey: "x",
      header: "",
    },
    {
      accessorKey: "y",
      header: "",
    },
    {
      accessorKey: "z",
      header: "",
    },
    {
      accessorKey: "w",
      header: "",
    },
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const downloadUrl = useFileUrl(file);

  useEffect(() => {
    setLoading(true);
    setError(null);

    if (!downloadUrl) {
      setError("File download url is not available");
      setLoading(false);
      return;
    }

    const defineColumn = (
      header: string,
    ): ColumnDef<Record<string, string>> => ({
      accessorKey: header,
      header: () => (
        <div className="h-full border-gray-200 border-t border-l p-1 font-inter-semibold-12px">
          {header}
        </div>
      ),
      cell: ({ row }) => (
        <div className="h-full truncate border-gray-200 border-l p-1 font-inter-normal-12px">
          {row.getValue(header)}
        </div>
      ),
      size: 150,
      minSize: 100,
      maxSize: 300,
    });

    // Intentionally using the default export to mock the parse function
    // eslint-disable-next-line import-x/no-named-as-default-member
    papaparse.parse(downloadUrl, {
      download: true,
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
      transform: (value) => value.trim(),
      complete: (results) => {
        if (results.errors.length > 0) {
          setError(results.errors[0].message);
          return;
        }

        const headers = results.meta.fields || [];
        setColumns(headers.map(defineColumn));
        setData(results.data as Record<string, string>[]);
        setLoading(false);
      },
      error: (error) => {
        setError(error.message);
        setLoading(false);
      },
    });
  }, [downloadUrl]);

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gray-50">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-white pt-2" data-loc="csv-viewer">
      <TableComp
        columns={columns}
        data={data}
        headerPropsGetter={() => ({
          classNameOverrides: "bg-gray-50",
        })}
        isLoading={loading}
        loadingRowsCount={9}
        rowClassName="hover:bg-gray-50"
        variant="default"
      />
    </div>
  );
};

const TextViewer: React.FC<{ file: FileAttributes | File }> = ({ file }) => {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const downloadUrl = useFileUrl(file);

  useEffect(() => {
    setLoading(true);
    setIsError(false);

    if (!downloadUrl) {
      setIsError(true);
      setLoading(false);
      return;
    }

    fetch(downloadUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Could not fetch file");
        }
        return response.text();
      })
      .then((text) => setText(text))
      .catch(() => setIsError(true))
      .finally(() => setLoading(false));
  }, [downloadUrl]);

  if (loading) {
    return <FallbackViewer message={undefined} state="loading" />;
  }

  if (isError) {
    return <FallbackViewer message={undefined} state="error" />;
  }

  return (
    <div className="h-full w-full bg-white" data-loc="text-viewer">
      <pre className="h-full overflow-auto whitespace-pre-wrap break-words text-sm">
        {text}
      </pre>
    </div>
  );
};

const JSONViewer: React.FC<{ file: FileAttributes | File }> = ({ file }) => {
  const [json, setJson] = useState<GenericObject>({});
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const downloadUrl = useFileUrl(file);

  useEffect(() => {
    setLoading(true);
    setIsError(false);

    if (!downloadUrl) {
      setIsError(true);
      setLoading(false);
      return;
    }

    fetch(downloadUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Could not fetch file");
        }
        return response.json();
      })
      .then((json) => setJson(json))
      .catch(() => setIsError(true))
      .finally(() => setLoading(false));
  }, [downloadUrl]);

  if (loading) {
    return <FallbackViewer message={undefined} state="loading" />;
  }

  if (isError) {
    return <FallbackViewer message={undefined} state="error" />;
  }

  return (
    <ObjectDetailPane
      dataLoc="json-file-viewer"
      defaultInspectDepth={3}
      innerDimensionClass="h-full"
      jsonObject={json}
      enableClipboard
    />
  );
};

const XLSXViewer: React.FC<{ file: FileAttributes | File }> = ({ file }) => {
  const downloadUrl = useFileUrl(file);
  const xlsxQuery = useQuery({
    queryKey: ["xlsx", downloadUrl],
    queryFn: async () => {
      if (!downloadUrl) {
        throw new Error("File download url is not available");
      }

      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error("Failed to fetch file");

      const arrayBuffer = await response.arrayBuffer();
      const workbook = xlsxRead(arrayBuffer, { type: "array" });

      const sheetsData = workbook.SheetNames.map((sheetName: string) => ({
        name: sheetName,
        data: xlsxUtils.sheet_to_json(workbook.Sheets[sheetName], {
          header: 1,
          defval: "",
          blankrows: false,
        }),
      }));

      const activeSheet = sheetsData[0];
      const rows = activeSheet.data as unknown[][];

      if (rows.length === 0) {
        throw new Error("No data found in spreadsheet");
      }

      const columns = rows[0].map((_, index) => ({
        accessorKey: index.toString(),
        header: () => (
          <div className="h-full border-gray-200 border-t border-l p-1 font-inter-semibold-12px">
            {rows[0][index]?.toString() || `Column ${index + 1}`}
          </div>
        ),
        cell: ({ row }: { row: Row<Record<string, string>> }) => (
          <div className="h-full truncate border-gray-200 border-l p-1 font-inter-normal-12px">
            {row.getValue(index.toString())}
          </div>
        ),
        size: 150,
        minSize: 100,
        maxSize: 300,
      }));

      const tableData = rows
        .slice(1)
        .map((row) =>
          Object.fromEntries(
            row.map((cell, index) => [
              index.toString(),
              cell?.toString() ?? "",
            ]),
          ),
        );

      return {
        columns,
        data: tableData,
        sheetNames: workbook.SheetNames,
        activeSheetName: activeSheet.name,
      };
    },
  });

  if (xlsxQuery.error) {
    return (
      <FallbackViewer
        message={(xlsxQuery.error as Error).message}
        state="error"
      />
    );
  }

  return (
    <div className="h-full w-full bg-white pt-2" data-loc="xlsx-viewer">
      <TableComp
        columns={xlsxQuery.data?.columns ?? []}
        data={xlsxQuery.data?.data ?? []}
        headerPropsGetter={() => ({
          classNameOverrides: "bg-gray-50",
        })}
        isLoading={xlsxQuery.isPending}
        loadingRowsCount={9}
        rowClassName="hover:bg-gray-50"
        variant="default"
      />
    </div>
  );
};

const FallbackViewer: React.FC<{
  message?: string;
  state: "error" | "loading" | "placeholder";
}> = ({ message, state }) => {
  const getBackgroundColor = () => {
    switch (state) {
      case "error":
        return "bg-red-100";
      case "loading":
        return "bg-indigo-100";
      case "placeholder":
        return "bg-gray-100";
    }
  };

  return (
    <div className="flex h-full w-full flex-col items-center justify-center bg-gray-50">
      <div
        className={`mb-2 flex h-12 w-12 items-center justify-center rounded-lg ${getBackgroundColor()}`}
      >
        <div className="text-4xl text-gray-400">
          {state === "error" && (
            <Icon color="text-red-600" icon={faExclamationCircle} />
          )}
          {state === "loading" && <Icon icon={faSpinner} spin />}
          {state === "placeholder" && <Icon icon={faFile} />}
        </div>
      </div>
      {state === "error" && (
        <>
          <span className="mb-1 font-inter-semibold-13px text-gray-800">
            File cannot be viewed
          </span>
          <span className="font-inter-normal-12px text-gray-500">
            {message}
          </span>
        </>
      )}
      {state === "loading" && (
        <span className="mb-1 font-inter-semibold-13px text-gray-800">
          Loading file...
        </span>
      )}
      {state === "placeholder" && (
        <>
          <span className="mb-1 font-inter-semibold-13px text-gray-800">
            Placeholder file
          </span>
          <span className="font-inter-normal-12px text-gray-500">
            Your file will be visible here
          </span>
        </>
      )}
    </div>
  );
};

export { FileViewer };
